import {
  type CampaignReward,
  type CampaignRewardBadge,
  type CampaignTypeConfig,
  type InfluencerCampaign,
  type InfluencerCampaignPlatform,
  InfluencerCampaignType,
} from "@/marketing/models/influencerCampaign";

export interface CardFieldsFormState {
  types: InfluencerCampaignType[];
  platforms: InfluencerCampaignPlatform[];
  tagline: string;
  rewardFixed: string;
  rewardCommission: string;
  rewardBonus: string;
  productWorth: string;
  rewardBadges: CampaignRewardBadge[];
  // BUS-315 creator-choice: how many SKUs the creator picks from the pool (blank = fixed, receive all).
  productChoiceCount: string;
  // BUS-315 giveaway typeConfig (only applied when Giveaway type is selected).
  giveawayOpenGlobally: boolean;
  giveawayRequireContentForReward: boolean;
}

export function emptyCardFieldsForm(): CardFieldsFormState {
  return {
    types: [],
    platforms: [],
    tagline: "",
    rewardFixed: "",
    rewardCommission: "",
    rewardBonus: "",
    productWorth: "",
    rewardBadges: [],
    productChoiceCount: "",
    giveawayOpenGlobally: false,
    giveawayRequireContentForReward: false,
  };
}

export function readCardFieldsForm(campaign: InfluencerCampaign | undefined): CardFieldsFormState {
  const config = campaign?.typeConfig;
  return {
    types: campaign?.types ?? [],
    platforms: campaign?.platforms ?? [],
    tagline: campaign?.tagline ?? "",
    rewardFixed: campaign?.reward?.fixed != null ? String(campaign.reward.fixed) : "",
    rewardCommission:
      campaign?.reward?.commission != null ? String(campaign.reward.commission) : "",
    rewardBonus: campaign?.reward?.bonus != null ? String(campaign.reward.bonus) : "",
    productWorth: campaign?.reward?.productWorth != null ? String(campaign.reward.productWorth) : "",
    rewardBadges: campaign?.reward?.badges ?? [],
    productChoiceCount:
      campaign?.reward?.productChoiceCount != null ? String(campaign.reward.productChoiceCount) : "",
    giveawayOpenGlobally: config?.openGlobally ?? false,
    giveawayRequireContentForReward: config?.requireContentForReward ?? false,
  };
}

export interface BuiltCardFields {
  types?: InfluencerCampaignType[];
  typeConfig?: CampaignTypeConfig;
  platforms?: InfluencerCampaignPlatform[];
  tagline?: string;
  reward?: CampaignReward;
}

function parseNonNegativeNumber(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

// productChoiceCount is a SKU count: only a positive integer is meaningful (blank/0/fractional = unset = fixed, receive all).
function parsePositiveInt(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

export function buildCardFields(form: CardFieldsFormState): BuiltCardFields {
  const reward: CampaignReward = {};
  const fixed = parseNonNegativeNumber(form.rewardFixed);
  if (fixed !== undefined) reward.fixed = fixed;
  const commission = parseNonNegativeNumber(form.rewardCommission);
  if (commission !== undefined) reward.commission = Math.min(commission, 100);
  const bonus = parseNonNegativeNumber(form.rewardBonus);
  if (bonus !== undefined) reward.bonus = bonus;
  const productWorth = parseNonNegativeNumber(form.productWorth);
  if (productWorth !== undefined) reward.productWorth = productWorth;
  if (form.rewardBadges.length > 0) reward.badges = form.rewardBadges;
  const choiceCount = parsePositiveInt(form.productChoiceCount);
  if (choiceCount !== undefined) reward.productChoiceCount = choiceCount;

  const built: BuiltCardFields = {
    types: form.types,
    platforms: form.platforms,
    tagline: form.tagline.trim(),
    reward,
  };

  // typeConfig only applies to Giveaway-type campaigns; omit it otherwise.
  if (form.types.includes(InfluencerCampaignType.Giveaway)) {
    built.typeConfig = {
      type: InfluencerCampaignType.Giveaway,
      openGlobally: form.giveawayOpenGlobally,
      requireContentForReward: form.giveawayRequireContentForReward,
    };
  }

  return built;
}
